import { useEffect, useState } from 'react';

import { Widgets } from '@mui/icons-material';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

import { useDesignerApp } from '../../../UserInterfaceDesigner/Contexts/DesignerAppContext';
import { Bundle } from '../../../UserInterfaceDesigner/Models/Bundle';
import { SharedComponentModel } from '../../../UserInterfaceDesigner/Models/SharedComponentModel';
import { SharedComponentRefModel } from '../../Models/SharedComponentRefModel';
import ListWithFilter from '../ListWithFilter';

type SharedComponentInputDialogProps = {
	open: boolean;
	setOpen: (open: boolean) => void;
	sharedComponentRef: SharedComponentRefModel | undefined;
	onSharedComponentRefChange: (sharedComponentRef: SharedComponentRefModel | undefined) => void;
	/** Bundle with shared components (from context or prop) */
	bundle?: Bundle | null;
};

export function SharedComponentInputDialog({ open, setOpen, sharedComponentRef, onSharedComponentRefChange, bundle }: SharedComponentInputDialogProps) {
	const [selectedId, setSelectedId] = useState<string | undefined>(sharedComponentRef?.Id);
	const designerBundle = useDesignerApp();

	// Use provided bundle, or fall back to designer context bundle
	const activeBundle = bundle ?? designerBundle.bundle;
	const sharedComponents: SharedComponentModel[] = activeBundle?.SharedComponents || [];

	useEffect(() => {
		if (open) {
			setSelectedId(sharedComponentRef?.Id);
		}
	}, [open, sharedComponentRef]);

	const handleSelect = (component: SharedComponentModel) => {
		setSelectedId(component.Id);
	};

	const handleSave = () => {
		if (selectedId) {
			const component = sharedComponents.find((c: SharedComponentModel) => c.Id === selectedId);
			onSharedComponentRefChange({
				Id: selectedId,
				Name: component?.Name,
			});
		}
		setOpen(false);
	};

	return (
		<Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
			<DialogTitle>Select Shared Component</DialogTitle>
			<DialogContent sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
				{sharedComponents.length > 0 ? (
					<ListWithFilter
						items={sharedComponents}
						selectedItemId={selectedId}
						onSelected={handleSelect}
						placeholder="Search shared components"
						getDisplayName={(component) => component.Name}
						getKey={(component) => component.Id}
						icon={() => <Widgets fontSize="small" />}
						settingsKey="sharedComponentInputDialog"
						sx={{ flex: 1, minHeight: 0 }}
					/>
				) : (
					<Box sx={{ p: 2, textAlign: 'center' }}>
						<Typography color="text.secondary">No shared components available. Please add shared components to the bundle first.</Typography>
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={() => setOpen(false)}>Cancel</Button>
				<Button onClick={handleSave} variant="contained" disabled={!selectedId}>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
}
